/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  CompetitorListDto,
  CompetitorPostListDto,
  CompetitorStatsDto,
  CompetitorSuggestionListDto,
  GetCompetitorStatsParams,
  ListCompetitorPostsParams,
  ReplaceCompetitorsDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The competitors, in order, with their tracked accounts and the plan's allowance.
 */
export const listCompetitors = (id: string, signal?: AbortSignal) => {
  return customInstance<CompetitorListDto>({
    url: `/api/v1/workspaces/${id}/competitors`,
    method: 'GET',
    signal,
  });
};

export const getListCompetitorsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/competitors`] as const;
};

export const getListCompetitorsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCompetitors>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitors>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCompetitorsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCompetitors>>> = ({
    signal,
  }) => listCompetitors(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listCompetitors>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListCompetitorsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCompetitors>>
>;
export type ListCompetitorsQueryError = ErrorType<ApiErrorDto>;

export function useListCompetitors<
  TData = Awaited<ReturnType<typeof listCompetitors>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitors>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCompetitors>>,
          TError,
          Awaited<ReturnType<typeof listCompetitors>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCompetitors<
  TData = Awaited<ReturnType<typeof listCompetitors>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitors>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCompetitors>>,
          TError,
          Awaited<ReturnType<typeof listCompetitors>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCompetitors<
  TData = Awaited<ReturnType<typeof listCompetitors>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitors>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The competitors, in order, with their tracked accounts and the plan's allowance.
 */

export function useListCompetitors<
  TData = Awaited<ReturnType<typeof listCompetitors>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitors>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCompetitorsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Replace the competitor list; new TikTok / Instagram handles start syncing.
 */
export const replaceCompetitors = (
  id: string,
  replaceCompetitorsDto: BodyType<ReplaceCompetitorsDto>,
  signal?: AbortSignal,
) => {
  return customInstance<CompetitorListDto>({
    url: `/api/v1/workspaces/${id}/competitors`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: replaceCompetitorsDto,
    signal,
  });
};

export const getReplaceCompetitorsMutationKey = () => ['replaceCompetitors'] as const;

export const getReplaceCompetitorsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof replaceCompetitors>>,
    TError,
    ReplaceCompetitorsMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof replaceCompetitors>>,
  TError,
  ReplaceCompetitorsMutationVariables,
  TContext
> => {
  const mutationKey = getReplaceCompetitorsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof replaceCompetitors>>,
    ReplaceCompetitorsMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return replaceCompetitors(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReplaceCompetitorsMutationResult = NonNullable<
  Awaited<ReturnType<typeof replaceCompetitors>>
>;
export type ReplaceCompetitorsMutationBody = BodyType<ReplaceCompetitorsDto>;
export type ReplaceCompetitorsMutationError = ErrorType<ApiErrorDto>;
export type ReplaceCompetitorsMutationVariables = {
  id: string;
  data: BodyType<ReplaceCompetitorsDto>;
};

/**
 * @summary Replace the competitor list; new TikTok / Instagram handles start syncing.
 */
export const useReplaceCompetitors = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof replaceCompetitors>>,
      TError,
      ReplaceCompetitorsMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof replaceCompetitors>>,
  TError,
  ReplaceCompetitorsMutationVariables,
  TContext
> => {
  return useMutation(getReplaceCompetitorsMutationOptions(options), queryClient);
};
/**
 * @summary Each tracked account's followers, usual views and winners over the last 30, 90 or 365 days.
 */
export const getCompetitorStats = (
  id: string,
  params?: GetCompetitorStatsParams,
  signal?: AbortSignal,
) => {
  return customInstance<CompetitorStatsDto>({
    url: `/api/v1/workspaces/${id}/competitors/stats`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetCompetitorStatsQueryKey = (
  id: string,
  params?: GetCompetitorStatsParams,
) => {
  return [
    `/api/v1/workspaces/${id}/competitors/stats`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetCompetitorStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getCompetitorStats>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: GetCompetitorStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCompetitorStats>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCompetitorStatsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCompetitorStats>>> = ({
    signal,
  }) => getCompetitorStats(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCompetitorStats>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetCompetitorStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCompetitorStats>>
>;
export type GetCompetitorStatsQueryError = ErrorType<ApiErrorDto>;

export function useGetCompetitorStats<
  TData = Awaited<ReturnType<typeof getCompetitorStats>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: undefined | GetCompetitorStatsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCompetitorStats>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCompetitorStats>>,
          TError,
          Awaited<ReturnType<typeof getCompetitorStats>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCompetitorStats<
  TData = Awaited<ReturnType<typeof getCompetitorStats>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: GetCompetitorStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCompetitorStats>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCompetitorStats>>,
          TError,
          Awaited<ReturnType<typeof getCompetitorStats>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCompetitorStats<
  TData = Awaited<ReturnType<typeof getCompetitorStats>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: GetCompetitorStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCompetitorStats>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Each tracked account's followers, usual views and winners over the last 30, 90 or 365 days.
 */

export function useGetCompetitorStats<
  TData = Awaited<ReturnType<typeof getCompetitorStats>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: GetCompetitorStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCompetitorStats>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCompetitorStatsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Read a competitor's accounts again now (at most every 6 hours).
 */
export const refreshCompetitor = (
  id: string,
  competitorId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/competitors/${competitorId}/refresh`,
    method: 'POST',
    signal,
  });
};

export const getRefreshCompetitorMutationKey = () => ['refreshCompetitor'] as const;

export const getRefreshCompetitorMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refreshCompetitor>>,
    TError,
    RefreshCompetitorMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof refreshCompetitor>>,
  TError,
  RefreshCompetitorMutationVariables,
  TContext
> => {
  const mutationKey = getRefreshCompetitorMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof refreshCompetitor>>,
    RefreshCompetitorMutationVariables
  > = (props) => {
    const { id, competitorId } = props ?? {};

    return refreshCompetitor(id, competitorId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefreshCompetitorMutationResult = NonNullable<
  Awaited<ReturnType<typeof refreshCompetitor>>
>;

export type RefreshCompetitorMutationError = ErrorType<ApiErrorDto>;
export type RefreshCompetitorMutationVariables = { id: string; competitorId: string };

/**
 * @summary Read a competitor's accounts again now (at most every 6 hours).
 */
export const useRefreshCompetitor = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof refreshCompetitor>>,
      TError,
      RefreshCompetitorMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof refreshCompetitor>>,
  TError,
  RefreshCompetitorMutationVariables,
  TContext
> => {
  return useMutation(getRefreshCompetitorMutationOptions(options), queryClient);
};
/**
 * @summary Accounts like the ones you track, those related to the most of them first.
 */
export const listCompetitorSuggestions = (id: string, signal?: AbortSignal) => {
  return customInstance<CompetitorSuggestionListDto>({
    url: `/api/v1/workspaces/${id}/competitor-suggestions`,
    method: 'GET',
    signal,
  });
};

export const getListCompetitorSuggestionsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/competitor-suggestions`] as const;
};

export const getListCompetitorSuggestionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCompetitorSuggestions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCompetitorSuggestions>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCompetitorSuggestionsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCompetitorSuggestions>>> = ({
    signal,
  }) => listCompetitorSuggestions(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listCompetitorSuggestions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListCompetitorSuggestionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCompetitorSuggestions>>
>;
export type ListCompetitorSuggestionsQueryError = ErrorType<ApiErrorDto>;

export function useListCompetitorSuggestions<
  TData = Awaited<ReturnType<typeof listCompetitorSuggestions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCompetitorSuggestions>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCompetitorSuggestions>>,
          TError,
          Awaited<ReturnType<typeof listCompetitorSuggestions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCompetitorSuggestions<
  TData = Awaited<ReturnType<typeof listCompetitorSuggestions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCompetitorSuggestions>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCompetitorSuggestions>>,
          TError,
          Awaited<ReturnType<typeof listCompetitorSuggestions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCompetitorSuggestions<
  TData = Awaited<ReturnType<typeof listCompetitorSuggestions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCompetitorSuggestions>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Accounts like the ones you track, those related to the most of them first.
 */

export function useListCompetitorSuggestions<
  TData = Awaited<ReturnType<typeof listCompetitorSuggestions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCompetitorSuggestions>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCompetitorSuggestionsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Work them out again now (at most once a day).
 */
export const refreshCompetitorSuggestions = (id: string, signal?: AbortSignal) => {
  return customInstance<CompetitorSuggestionListDto>({
    url: `/api/v1/workspaces/${id}/competitor-suggestions/refresh`,
    method: 'POST',
    signal,
  });
};

export const getRefreshCompetitorSuggestionsMutationKey = () =>
  ['refreshCompetitorSuggestions'] as const;

export const getRefreshCompetitorSuggestionsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refreshCompetitorSuggestions>>,
    TError,
    RefreshCompetitorSuggestionsMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof refreshCompetitorSuggestions>>,
  TError,
  RefreshCompetitorSuggestionsMutationVariables,
  TContext
> => {
  const mutationKey = getRefreshCompetitorSuggestionsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof refreshCompetitorSuggestions>>,
    RefreshCompetitorSuggestionsMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return refreshCompetitorSuggestions(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefreshCompetitorSuggestionsMutationResult = NonNullable<
  Awaited<ReturnType<typeof refreshCompetitorSuggestions>>
>;

export type RefreshCompetitorSuggestionsMutationError = ErrorType<ApiErrorDto>;
export type RefreshCompetitorSuggestionsMutationVariables = { id: string };

/**
 * @summary Work them out again now (at most once a day).
 */
export const useRefreshCompetitorSuggestions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof refreshCompetitorSuggestions>>,
      TError,
      RefreshCompetitorSuggestionsMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof refreshCompetitorSuggestions>>,
  TError,
  RefreshCompetitorSuggestionsMutationVariables,
  TContext
> => {
  return useMutation(
    getRefreshCompetitorSuggestionsMutationOptions(options),
    queryClient,
  );
};
/**
 * @summary Track it as a competitor.
 */
export const trackCompetitorSuggestion = (
  id: string,
  suggestionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/competitor-suggestions/${suggestionId}/track`,
    method: 'POST',
    signal,
  });
};

export const getTrackCompetitorSuggestionMutationKey = () =>
  ['trackCompetitorSuggestion'] as const;

export const getTrackCompetitorSuggestionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof trackCompetitorSuggestion>>,
    TError,
    TrackCompetitorSuggestionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof trackCompetitorSuggestion>>,
  TError,
  TrackCompetitorSuggestionMutationVariables,
  TContext
> => {
  const mutationKey = getTrackCompetitorSuggestionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof trackCompetitorSuggestion>>,
    TrackCompetitorSuggestionMutationVariables
  > = (props) => {
    const { id, suggestionId } = props ?? {};

    return trackCompetitorSuggestion(id, suggestionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type TrackCompetitorSuggestionMutationResult = NonNullable<
  Awaited<ReturnType<typeof trackCompetitorSuggestion>>
>;

export type TrackCompetitorSuggestionMutationError = ErrorType<ApiErrorDto>;
export type TrackCompetitorSuggestionMutationVariables = {
  id: string;
  suggestionId: string;
};

/**
 * @summary Track it as a competitor.
 */
export const useTrackCompetitorSuggestion = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof trackCompetitorSuggestion>>,
      TError,
      TrackCompetitorSuggestionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof trackCompetitorSuggestion>>,
  TError,
  TrackCompetitorSuggestionMutationVariables,
  TContext
> => {
  return useMutation(getTrackCompetitorSuggestionMutationOptions(options), queryClient);
};
/**
 * @summary Not interested - it won't be suggested again.
 */
export const dismissCompetitorSuggestion = (
  id: string,
  suggestionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/competitor-suggestions/${suggestionId}/dismiss`,
    method: 'POST',
    signal,
  });
};

export const getDismissCompetitorSuggestionMutationKey = () =>
  ['dismissCompetitorSuggestion'] as const;

export const getDismissCompetitorSuggestionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof dismissCompetitorSuggestion>>,
    TError,
    DismissCompetitorSuggestionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof dismissCompetitorSuggestion>>,
  TError,
  DismissCompetitorSuggestionMutationVariables,
  TContext
> => {
  const mutationKey = getDismissCompetitorSuggestionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof dismissCompetitorSuggestion>>,
    DismissCompetitorSuggestionMutationVariables
  > = (props) => {
    const { id, suggestionId } = props ?? {};

    return dismissCompetitorSuggestion(id, suggestionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DismissCompetitorSuggestionMutationResult = NonNullable<
  Awaited<ReturnType<typeof dismissCompetitorSuggestion>>
>;

export type DismissCompetitorSuggestionMutationError = ErrorType<ApiErrorDto>;
export type DismissCompetitorSuggestionMutationVariables = {
  id: string;
  suggestionId: string;
};

/**
 * @summary Not interested - it won't be suggested again.
 */
export const useDismissCompetitorSuggestion = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof dismissCompetitorSuggestion>>,
      TError,
      DismissCompetitorSuggestionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof dismissCompetitorSuggestion>>,
  TError,
  DismissCompetitorSuggestionMutationVariables,
  TContext
> => {
  return useMutation(getDismissCompetitorSuggestionMutationOptions(options), queryClient);
};
/**
 * @summary What's working for your competitors: their tracked posts beating their
usual views (by default 2× in the last 7 days), best first, and how far
along tracking is.
 */
export const listCompetitorPosts = (
  id: string,
  params?: ListCompetitorPostsParams,
  signal?: AbortSignal,
) => {
  return customInstance<CompetitorPostListDto>({
    url: `/api/v1/workspaces/${id}/competitor-posts`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListCompetitorPostsQueryKey = (
  id: string,
  params?: ListCompetitorPostsParams,
) => {
  return [
    `/api/v1/workspaces/${id}/competitor-posts`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListCompetitorPostsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCompetitorPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListCompetitorPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitorPosts>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCompetitorPostsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCompetitorPosts>>> = ({
    signal,
  }) => listCompetitorPosts(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listCompetitorPosts>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListCompetitorPostsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCompetitorPosts>>
>;
export type ListCompetitorPostsQueryError = ErrorType<ApiErrorDto>;

export function useListCompetitorPosts<
  TData = Awaited<ReturnType<typeof listCompetitorPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: undefined | ListCompetitorPostsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitorPosts>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCompetitorPosts>>,
          TError,
          Awaited<ReturnType<typeof listCompetitorPosts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCompetitorPosts<
  TData = Awaited<ReturnType<typeof listCompetitorPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListCompetitorPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitorPosts>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCompetitorPosts>>,
          TError,
          Awaited<ReturnType<typeof listCompetitorPosts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCompetitorPosts<
  TData = Awaited<ReturnType<typeof listCompetitorPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListCompetitorPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitorPosts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary What's working for your competitors: their tracked posts beating their
usual views (by default 2× in the last 7 days), best first, and how far
along tracking is.
 */

export function useListCompetitorPosts<
  TData = Awaited<ReturnType<typeof listCompetitorPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListCompetitorPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCompetitorPosts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCompetitorPostsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}
